import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import RedoIcon from "@mui/icons-material/Redo";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  IconButton as MuiIconButton,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import { SelectDateRange } from "components/forms";
import { SelectCurrency } from "components/reports/Select-currency/SelectCurrency";
import { useInvestorsSelector } from "store";
import type { DateRangeValue, Filters, ReportOrientation } from "store/types";

import { GlobalFilterBar } from "./GlobalFilterBar";
import { ToggleButtons } from "./ToggleButtons";

interface Props {
  reportTitle: string;
  onTitleChange: (value: string) => void;
  dateRange: DateRangeValue | null;
  onDateRangeChange: (value: DateRangeValue) => void;
  orientation: ReportOrientation;
  onOrientationChange: (value: ReportOrientation) => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onPreview: () => void;
  onExportPDF: () => void;
  onBatchExport: (investors: string[]) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

const ORIENTATION_OPTIONS: { value: ReportOrientation; label: string }[] = [
  { value: "portrait", label: "Portrait" },
  { value: "landscape", label: "Landscape" }
];

export const TopBar: React.FC<Props> = ({
  reportTitle,
  onTitleChange,
  dateRange,
  onDateRangeChange,
  orientation,
  onOrientationChange,
  filters,
  onFiltersChange,
  onPreview,
  onExportPDF,
  onBatchExport,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  zoom,
  onZoomChange
}) => {
  const investors = useInvestorsSelector();
  const ALL_INVESTORS = investors.map((inv) => inv.name);

  return (
    <Box sx={{ bgcolor: "#FFFFFF", borderBottom: "1px solid #dbe8f5", flexShrink: 0, zIndex: 20 }}>
      {/* Row 1: branding, title, orientation, undo/redo, actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          p: "14px 20px 12px",
          borderBottom: "1px solid #dbe8f5",
          flexWrap: "wrap"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              bgcolor: "#071f35",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#c09560",
              fontSize: 12,
              fontWeight: 800
            }}
          >
            A
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35", letterSpacing: "0.08em" }}>
              ALTIUS
            </Typography>
            <Typography sx={{ fontSize: 10, color: "#496682" }}>Report Builder</Typography>
          </Box>
        </Box>

        <Box
          component="input"
          value={reportTitle}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
          placeholder="Report title"
          sx={{
            flex: 1,
            minWidth: 240,
            border: "none",
            outline: "none",
            fontSize: 18,
            fontWeight: 650,
            color: "#071f35",
            bgcolor: "transparent",
            fontFamily: "inherit"
          }}
        />

        <ToggleButtons options={ORIENTATION_OPTIONS} value={orientation} onChange={onOrientationChange} />

        <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <MuiIconButton
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            <UndoIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
          <MuiIconButton
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            <RedoIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          <MuiIconButton
            onClick={() => onZoomChange(Math.max(0.5, Math.round((zoom - 0.1) * 100) / 100))}
            disabled={zoom <= 0.5}
            title="Zoom out"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              fontSize: 16,
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            &minus;
          </MuiIconButton>
          <Box
            component="button"
            onClick={() => onZoomChange(1)}
            title="Reset zoom to 100%"
            sx={{
              minWidth: 48,
              height: 32,
              border: "1px solid #dbe8f5",
              borderRadius: "6px",
              bgcolor: "#FFFFFF",
              color: "#496682",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "inherit",
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            {Math.round(zoom * 100)}%
          </Box>
          <MuiIconButton
            onClick={() => onZoomChange(Math.min(1, Math.round((zoom + 0.1) * 100) / 100))}
            disabled={zoom >= 1}
            title="Zoom in"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              fontSize: 16,
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            +
          </MuiIconButton>
        </Box>

        <Button
          onClick={onPreview}
          variant="outlined"
          sx={{
            p: "8px 14px",
            borderRadius: "999px",
            bgcolor: "#FFFFFF",
            color: "#071f35",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
            flexShrink: 0,
            "&:hover": { bgcolor: "#f6f8fc" }
          }}
        >
          Preview
        </Button>

        <ExportButtonGroup onExportPDF={onExportPDF} onBatchExport={onBatchExport} investors={ALL_INVESTORS} />
      </Box>

      {/* Row 2: filters + period/currency */}
      <Box
        sx={{
          p: "10px 20px",
          bgcolor: "#f6f8fc",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap"
        }}
      >
        <GlobalFilterBar filters={filters} onFiltersChange={onFiltersChange} />
        <Box sx={{ flex: 1 }} />
        <SelectDateRange
          formControlProps={{ sx: { minWidth: "200px" } }}
          selectProps={{
            startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
          }}
          label=""
          value={dateRange}
          onChange={onDateRangeChange}
        />
        <SelectCurrency />
      </Box>
    </Box>
  );
};

interface ExportButtonGroupProps {
  onExportPDF: () => void;
  onBatchExport: (investors: string[]) => void;
  investors: readonly string[];
}

const ExportButtonGroup: React.FC<ExportButtonGroupProps> = ({ onExportPDF, onBatchExport, investors }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedInvestors, setSelectedInvestors] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const handleToggleInvestor = (investor: string) => {
    setSelectedInvestors((prev) =>
      prev.includes(investor) ? prev.filter((i) => i !== investor) : [...prev, investor]
    );
  };

  const handleBatchExport = () => {
    if (!selectedInvestors.length) return;
    setAnchorEl(null);
    onBatchExport(selectedInvestors);
    setSelectedInvestors([]);
  };

  return (
    <Box sx={{ position: "relative", flexShrink: 0, display: "flex", gap: 0 }}>
      <Button
        onClick={onExportPDF}
        sx={{
          p: "8px 14px",
          border: "none",
          borderRadius: "999px 0 0 999px",
          bgcolor: "#071f35",
          color: "#FFFFFF",
          fontSize: 12,
          fontWeight: 700,
          textTransform: "none",
          boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
          "&:hover": { bgcolor: "#0a2e4f" }
        }}
      >
        Export PDF
      </Button>
      <Button
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        title="Batch export by investor"
        sx={{
          p: "8px 8px",
          border: "none",
          minWidth: 0,
          borderLeft: "1px solid rgba(255,255,255,0.25)",
          borderRadius: "0 999px 999px 0",
          bgcolor: "#071f35",
          color: "#FFFFFF",
          fontSize: 10,
          boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
          "&:hover": { bgcolor: "#0a2e4f" }
        }}
      >
        <Box component="span" sx={{ fontSize: 8 }}>
          {open ? "\u25B2" : "\u25BC"}
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              border: "1px solid #dbe8f5",
              boxShadow: "0 12px 32px rgba(7,31,53,0.14)",
              width: 280,
              overflow: "hidden"
            }
          }
        }}
      >
        <Box sx={{ p: "14px 16px 10px", borderBottom: "1px solid #dbe8f5" }}>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#071f35" }}>Batch Export</Typography>
            <IconButton onClick={() => setAnchorEl(null)}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: 11, color: "#496682", mt: "2px" }}>Generate one PDF per investor</Typography>
        </Box>

        <Box sx={{ maxHeight: 220, overflowY: "auto", p: "6px 0" }}>
          {investors.map((investor) => {
            const checked = selectedInvestors.includes(investor);
            return (
              <FormControlLabel
                key={investor}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggleInvestor(investor)}
                    sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                }
                label={investor}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  m: 0,
                  p: "5px 16px",
                  bgcolor: checked ? "#F6F8FC" : "transparent",
                  "& .MuiFormControlLabel-label": {
                    fontSize: 13,
                    color: "#071f35",
                    fontWeight: checked ? 600 : 400,
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ p: "10px 16px", borderTop: "1px solid #dbe8f5", display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            onClick={() => setSelectedInvestors(selectedInvestors.length === investors.length ? [] : [...investors])}
            sx={{ color: "#26A6AE", fontSize: 12, fontWeight: 600, textTransform: "none", p: "8px 14px", minWidth: 0 }}
          >
            {selectedInvestors.length === investors.length ? "Deselect all" : "Select all"}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleBatchExport}
            disabled={selectedInvestors.length === 0}
            sx={{
              p: "7px 14px",
              borderRadius: "999px",
              minWidth: 0,
              bgcolor: selectedInvestors.length > 0 ? "#071f35" : "#dbe8f5",
              color: selectedInvestors.length > 0 ? "#FFFFFF" : "#92a7bc",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: selectedInvestors.length > 0 ? "#0a2e4f" : "#dbe8f5" },
              "&.Mui-disabled": { bgcolor: "#dbe8f5", color: "#92a7bc" }
            }}
          >
            Export{" "}
            {selectedInvestors.length > 0
              ? `${selectedInvestors.length} PDF${selectedInvestors.length > 1 ? "s" : ""}`
              : "..."}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
